/**
 * Language Switcher — compact toggle button (EN / DE).
 * Sits in the top bar; no external library required.
 */
import { useLocale } from "@/contexts/LocaleContext";
import { analytics } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGUAGES = [
  { code: "en", label: "English (Intl.)", short: "EN" },
  { code: "en-US", label: "English (US)", short: "US" },
  { code: "de", label: "Deutsch", short: "DE" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "es", label: "Español", short: "ES" },
  { code: "it", label: "Italiano", short: "IT" },
  { code: "pt", label: "Português", short: "PT" },
] as const;

export function LanguageSwitcher() {
  const { locale, setLocale } = useLocale();
  const current = LANGUAGES.find((l) => l.code === locale) ?? LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
          aria-label="Switch language"
        >
          <Globe className="h-3.5 w-3.5" />
          <span>{current.short}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => { setLocale(lang.code); analytics.track("settings.language_changed", { new_locale: lang.code }); }}
            className={locale === lang.code ? "font-semibold text-primary" : ""}
          >
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
